import { type FC } from "react";
import {
  NumberField,
  Pulldown,
  TextArea,
  ValidationErrorMessage,
} from "@legacy_user_frontend/components/ui_parts";
import { TextButton } from "@root/shared/components/TextButton";
import * as styles from "@legacy_user_frontend/components/pages/mypage/profiles/user_career_edit/src/styles.css";
import { type OccupationProps } from "@legacy_user_frontend/components/pages/mypage/profiles/user_career_edit/src/user_career_edit_props";
import { DefinitionList } from "@legacy_user_frontend/components/pages/mypage/profiles/ui_parts/definition_list";
import { DeletableCassette } from "@legacy_user_frontend/components/pages/mypage/profiles/ui_parts/deletable_cassette";
import { useFormContext } from "react-hook-form";
import { Checkbox } from "@root/shared/components/Checkbox";
import { JOB_TYPE_CATEGORY } from "@root/constants/jobTypeCategories";

const shouldHideSalesFields = (id: number | string | null): boolean => {
  if (typeof id !== "number") return false;

  const hiddenCategories: number[] = [
    JOB_TYPE_CATEGORY.KAIGO_ID,
    JOB_TYPE_CATEGORY.OTHER_ID,
    JOB_TYPE_CATEGORY.SHIKA_ID,
    JOB_TYPE_CATEGORY.HOTEL_ID,
    JOB_TYPE_CATEGORY.RESTAURANT_ID,
    JOB_TYPE_CATEGORY.MEDICAL_ID,
  ];

  return hiddenCategories.includes(id);
};

export const Occupation: FC<OccupationProps> = ({
  careerIndex,
  fields,
  facilityTypesList,
  appendField,
  removeFacilityType,
  selectedJobTypeCategoryId,
  selectedJobTypeId,
  workPatternList,
  workPatterns,
  descriptionPlaceholder,
  yearsOfExperienceList,
}) => {
  const formName = `user_careers_update[user_careers_attributes][${careerIndex}]`;
  const { formState } = useFormContext();
  const formErrors = formState.errors?.user_careers_update?.user_careers_attributes;
  const occupationErrors = formErrors && formErrors[careerIndex];
  const careerErrors = occupationErrors && occupationErrors.user_careers_facility_types_attributes;

  return (
    <>
      <div className={styles.occupationOuter}>
        <DefinitionList>
          <dt>施設形態</dt>
          <dd>
            {fields.map((field, index) => {
              const facilityName = `${formName}[user_careers_facility_types_attributes][${index}]`;
              const facilityTypeError = careerErrors && careerErrors[index];
              const facilityTypeIdError = facilityTypeError && facilityTypeError.facility_type_id;
              const YearsOfExperienceError =
                facilityTypeError && facilityTypeError.years_of_experience;
              return (
                <DeletableCassette
                  key={field.id}
                  heading={`施設形態${index + 1}`}
                  handleOnClick={() => removeFacilityType(index)}
                >
                  <div className={styles.facilityTypesOuter}>
                    <div>
                      <Pulldown
                        name={`${facilityName}[facility_type_id]`}
                        placeholder="施設形態を選択"
                        list={facilityTypesList || []}
                        isError={!!facilityTypeIdError}
                      />
                      {facilityTypeIdError && (
                        <div className={styles.errorMessageOuter}>
                          <ValidationErrorMessage message={facilityTypeIdError.message} />
                        </div>
                      )}
                    </div>
                    <div>
                      <Pulldown
                        name={`${facilityName}[years_of_experience]`}
                        placeholder="経験年数"
                        list={yearsOfExperienceList}
                        isError={!!YearsOfExperienceError}
                      />
                      {YearsOfExperienceError && (
                        <div className={styles.errorMessageOuter}>
                          <ValidationErrorMessage message={YearsOfExperienceError.message} />
                        </div>
                      )}
                    </div>
                  </div>
                </DeletableCassette>
              );
            })}
          </dd>
        </DefinitionList>
        <div className={styles.addFacilityTypesButtonOuter}>
          <TextButton
            type="button"
            onClick={appendField}
            size="large"
            className={styles.addFacilityTypesButton}
          >
            施設形態を追加する
          </TextButton>
        </div>
      </div>
      <div className={styles.occupationOuter}>
        <DefinitionList>
          <>
            {!shouldHideSalesFields(selectedJobTypeCategoryId) && (
              <>
                {selectedJobTypeId !== JOB_TYPE_CATEGORY.OTHER_ID && (
                  <>
                    <dt>平均対応客数（個人実績）</dt>
                    <dd>
                      <div className={styles.smallInputOuter}>
                        <NumberField
                          name={`${formName}[average_number_of_customers]`}
                          placeholder="半角数字で入力"
                          min={0}
                        />
                      </div>
                      人 / 月
                      {occupationErrors && occupationErrors.average_number_of_customers && (
                        <div className={styles.errorMessageOuter}>
                          <ValidationErrorMessage
                            message={occupationErrors.average_number_of_customers.message}
                          />
                        </div>
                      )}
                    </dd>
                    <dt>平均新規顧客割合（個人実績）</dt>
                    <dd>
                      <div className={styles.smallInputOuter}>
                        <NumberField
                          name={`${formName}[average_percentage_of_new_customers]`}
                          placeholder="半角数字で入力"
                          min={0}
                        />
                      </div>
                      %
                      {occupationErrors && occupationErrors.average_percentage_of_new_customers && (
                        <div className={styles.errorMessageOuter}>
                          <ValidationErrorMessage
                            message={occupationErrors.average_percentage_of_new_customers.message}
                          />
                        </div>
                      )}
                    </dd>
                    <dt>平均指名割合（個人実績）</dt>
                    <dd>
                      <div className={styles.smallInputOuter}>
                        <NumberField
                          name={`${formName}[average_nomination_ratio]`}
                          placeholder="半角数字で入力"
                          min={0}
                        />
                      </div>
                      %
                      {occupationErrors && occupationErrors.average_nomination_ratio && (
                        <div className={styles.errorMessageOuter}>
                          <ValidationErrorMessage
                            message={occupationErrors.average_nomination_ratio.message}
                          />
                        </div>
                      )}
                    </dd>
                  </>
                )}
                <dt>平均顧客単価（個人実績）</dt>
                <dd>
                  <div className={styles.smallInputOuter}>
                    <NumberField
                      name={`${formName}[average_customer_spending]`}
                      placeholder="半角数字で入力"
                      min={0}
                    />
                  </div>
                  円
                  {occupationErrors && occupationErrors.average_customer_spending && (
                    <div className={styles.errorMessageOuter}>
                      <ValidationErrorMessage
                        message={occupationErrors.average_customer_spending.message}
                      />
                    </div>
                  )}
                </dd>
                <dt>平均売上（個人実績）</dt>
                <dd>
                  <div className={styles.smallInputOuter}>
                    <NumberField
                      name={`${formName}[average_personal_sales]`}
                      placeholder="半角数字で入力"
                      min={0}
                    />
                  </div>
                  円 / 月
                  {occupationErrors && occupationErrors.average_personal_sales && (
                    <div className={styles.errorMessageOuter}>
                      <ValidationErrorMessage
                        message={occupationErrors.average_personal_sales.message}
                      />
                    </div>
                  )}
                </dd>
              </>
            )}
            {selectedJobTypeCategoryId === JOB_TYPE_CATEGORY.KAIGO_ID && (
              <>
                <dt>勤務形態</dt>
                <dd>
                  <ul className={styles.list}>
                    {workPatternList.map(({ key, value }) => {
                      const id = `user_careers_update_user_careers_attributes_${careerIndex}__user_career_work_patterns_${key}`;
                      return (
                        <li key={key}>
                          <Checkbox
                            name={`${formName}[user_career_work_patterns][]`}
                            value={`${value}`}
                            id={id}
                            defaultChecked={workPatterns?.includes(value)}
                          />
                          <label className={styles.label} htmlFor={id}>
                            {key}
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                </dd>
              </>
            )}
            {!shouldHideSalesFields(selectedJobTypeCategoryId) && (
              <>
                <dt>客層</dt>
                <dd>
                  <TextArea
                    name={`${formName}[clientele]`}
                    placeholder="商業施設の中にある施設だったので、ファミリー層が多く、下は10代から上は80代まで、幅広い年代の方が来店されていました。"
                  />
                </dd>
              </>
            )}
            <dt>職務内容</dt>
            <dd>
              <TextArea
                name={`${formName}[description]`}
                placeholder={descriptionPlaceholder || ""}
                height={207}
              />
            </dd>
          </>
        </DefinitionList>
      </div>
    </>
  );
};
